import { createContext, useCallback, useContext, useMemo, useState } from "react";

/**
 * SkinModalContext
 * -----------------------------------------------------------------------------
 * Bridge between the legacy per-form modal components (PaymentProcessingModal,
 * TransactionStatusModal) and any skin that wants to render its own UI for
 * those states.
 *
 * The legacy modals look up this context with `useSkinModals()`. If the active
 * skin has registered a provider, the modals push their state through the
 * context and render `null` instead of their own DOM. The skin then renders
 * the appropriate overlay (e.g. NeoProcessingOverlay / NeoStatusScreen).
 *
 * If no provider is mounted (default Classic skin), `useSkinModals()` returns
 * `null` and the legacy modals render their original UI — fully backward
 * compatible.
 */
const SkinModalContext = createContext(null);

export const useSkinModals = () => useContext(SkinModalContext);

export const SkinModalProvider = ({ children }) => {
  // `processing` / `status` are either `null` (closed) or an opaque payload
  // describing what to render. The legacy modal components push their props
  // verbatim so the skin renders whatever data they had.
  const [processing, setProcessing] = useState(null);
  const [status, setStatus] = useState(null);

  const openProcessing  = useCallback((payload) => setProcessing(payload || {}), []);
  const closeProcessing = useCallback(() => setProcessing(null), []);

  const openStatus  = useCallback((payload) => setStatus(payload || {}), []);
  const closeStatus = useCallback(() => setStatus(null), []);

  const value = useMemo(
    () => ({
      processing,
      status,
      openProcessing,
      closeProcessing,
      openStatus,
      closeStatus,
    }),
    [processing, status, openProcessing, closeProcessing, openStatus, closeStatus]
  );

  return (
    <SkinModalContext.Provider value={value}>
      {children}
    </SkinModalContext.Provider>
  );
};

export default SkinModalContext;
